import { ReactNode, useEffect } from "react";
import ModalHeader from "./ModalHeader";
import ModalBody from "./ModalBody";
import ModalFooter from "./ModalFooter";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import useMobile from "src/hooks/useMobile";
import { createPortal } from "react-dom";

type ModalType = {
  children: ReactNode;
  width?: string;
  className?: string;
  onEscape: () => void;
  closeOnOverlayClick?: boolean;
};

type ModalSubcomponents = {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

const Modal: React.FC<ModalType> & ModalSubcomponents = ({
  children,
  width = "w-auto",
  className = "",
  onEscape = () => {},
  closeOnOverlayClick = true,
}) => {
  const { isMobile } = useMobile();

  useEffect(() => {
    const body = document?.getElementsByTagName("body")?.[0];
    body?.classList?.add?.("expenser-block-scroll");

    return () => {
      body?.classList?.remove?.("expenser-block-scroll");
    };
  }, []);

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={clsx([
          "fixed",
          "inset-0",
          "z-[100]",
          "flex",
          "items-center",
          "justify-center",
          "bg-black/20",
          "dark:bg-black/60",
          "backdrop-blur-sm",
        ])}
        onClick={() => closeOnOverlayClick && onEscape?.()}
        onKeyDown={({ key }) => key === "Escape" && onEscape?.()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -20 }}
          transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1], // easeOutCubic
          }}
          onClick={(e) => e?.stopPropagation?.()}
          className={clsx([
            "bg-white",
            "dark:bg-gray-800",
            "overflow-scroll",
            "rounded-md",
            "md:rounded-xl",
            "w-full",
            "md:w-auto",
            "max-w-[calc(100vw-32px)]",
            "md:max-w-[95vw]",
            "max-h-[calc(100vh-32px)]",
            "md:max-h-[95vh]",
            "xl:max-h-[98vh]",
            "shadow-sm",
            "md:shadow-md",
            "dark:shadow-gray-900",
            "dark:text-white",
            width,
            className,
          ])}
        >
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
